import { useLayoutEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSaveFine } from '../../hooks/useApi';
import { errorMessage } from '../../services/api';
import { toast } from '../../store';
import type { Fine, FineInput } from '../../types';
import { EUROPE_COUNTRIES, toDateInput } from '../../utils/helpers';
import { Button } from '../ui/Button';
import { Checkbox, Input, Select, Textarea } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { CarSelect, DriverSelect } from './EntitySelects';
import { PersonModeToggle, type PersonMode } from './PersonModeToggle';
import { t } from '../../i18n';

interface Values {
  car_id: string;
  driver_id: string;
  person_name: string;
  country: string;
  fine_date: string;
  amount: string;
  reason: string;
  reference: string;
  paid: boolean;
  paid_date: string;
  paid_by: string;
  notes: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  fine?: Fine | null;
}

const COUNTRY_OPTIONS = Object.entries(EUROPE_COUNTRIES).map(([code, name]) => ({ value: code, label: `${code} – ${name}` }));

export function FineFormModal({ isOpen, onClose, fine }: Props) {
  const save = useSaveFine();
  const [personMode, setPersonMode] = useState<PersonMode>('list');
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<Values>();

  useLayoutEffect(() => {
    if (!isOpen) return;
    setPersonMode(fine?.person_name ? 'name' : 'list');
    reset({
      car_id: fine ? String(fine.car_id) : '',
      driver_id: fine?.driver_id ? String(fine.driver_id) : '',
      person_name: fine?.person_name ?? '',
      country: fine?.country ?? 'SK',
      fine_date: fine?.fine_date ?? toDateInput(),
      amount: fine ? String(fine.amount) : '',
      reason: fine?.reason ?? '',
      reference: fine?.reference ?? '',
      paid: fine?.paid ?? false,
      paid_date: fine?.paid_date ?? '',
      paid_by: fine?.paid_by ?? 'company',
      notes: fine?.notes ?? '',
    });
  }, [isOpen, fine, reset]);

  const paid = watch('paid');

  const onSubmit = async (v: Values) => {
    const data: FineInput = {
      car_id: Number(v.car_id),
      driver_id: personMode === 'list' && v.driver_id ? Number(v.driver_id) : null,
      person_name: personMode === 'name' ? v.person_name.trim() || null : null,
      country: v.country,
      fine_date: v.fine_date,
      amount: Number(v.amount),
      reason: v.reason.trim() || null,
      reference: v.reference.trim() || null,
      paid: v.paid,
      paid_date: v.paid ? v.paid_date || null : null,
      paid_by: v.paid ? ((v.paid_by || null) as FineInput['paid_by']) : null,
      notes: v.notes.trim() || null,
    };
    try {
      await save.mutateAsync({ id: fine?.id, data });
      toast.success(fine ? t('Pokuta bola uložená') : t('Pokuta bola pridaná'));
      onClose();
    } catch (error) {
      toast.error(errorMessage(error, t('Pokutu sa nepodarilo uložiť')));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      busy={save.isPending}
      size="lg"
      title={fine ? t('Upraviť pokutu') : t('Nová pokuta')}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            {t('Zrušiť')}
          </Button>
          <Button type="submit" form="fine-form" loading={save.isPending}>
            {fine ? t('Uložiť') : t('Pridať')}
          </Button>
        </>
      }
    >
      <form id="fine-form" onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 gap-4 sm:grid-cols-2" noValidate>
        <CarSelect label={t('Vozidlo')} required error={errors.car_id?.message} {...register('car_id', { required: t('Vyberte vozidlo') })} />
        <Select label={t('Krajina')} required options={COUNTRY_OPTIONS} {...register('country')} />
        <Input label={t('Dátum priestupku')} type="date" required error={errors.fine_date?.message} {...register('fine_date', { required: t('Zadajte dátum') })} />
        <Input
          label={t('Suma (€)')}
          type="number"
          step="0.01"
          min={0}
          required
          error={errors.amount?.message}
          {...register('amount', { required: t('Zadajte sumu'), validate: (v) => Number(v) >= 0 || t('Nesmie byť záporná') })}
        />
        <div className="sm:col-span-2">
          <p className="mb-1.5 text-sm font-medium text-secondary-700">{t('Kto dostal pokutu')}</p>
          <PersonModeToggle value={personMode} onChange={setPersonMode} />
          {personMode === 'list' ? (
            <DriverSelect aria-label={t('Pracovník')} onlyActive={false} placeholder={t('Neurčený / zatiaľ nevieme')} {...register('driver_id')} />
          ) : (
            <Input aria-label={t('Meno a priezvisko')} placeholder={t('Meno a priezvisko')} {...register('person_name')} />
          )}
        </div>
        <Input label={t('Priestupok / dôvod')} placeholder={t('napr. prekročenie rýchlosti, parkovanie')} {...register('reason')} />
        <Input label={t('Číslo pokuty / spisu')} {...register('reference')} />
        <div className="rounded-lg border border-secondary-200 p-3 sm:col-span-2">
          <Checkbox label={t('Zaplatená')} {...register('paid')} />
          {paid && (
            <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input label={t('Dátum úhrady')} type="date" hint={t('Prázdne = dnes')} {...register('paid_date')} />
              <Select
                label={t('Hradil(a)')}
                options={[
                  { value: 'company', label: t('Firma') },
                  { value: 'driver', label: t('Vodič (zrazené / refakturované)') },
                ]}
                {...register('paid_by')}
              />
            </div>
          )}
        </div>
        <Textarea label={t('Poznámka')} rows={2} wrapperClassName="sm:col-span-2" {...register('notes')} />
      </form>
    </Modal>
  );
}
